import React, { useState, useEffect } from "react"
import { Box } from "@mui/system"
import PropTypes from "prop-types"
import { Grid } from "@mui/material"
import { useBoxSectionRating } from "../../hooks/use-ratings-use-reducer"
import { useRatingEmissaoTable } from "../../hooks/table-emission-hooks/use-last-rating-emission"
import { usePrecosProcessados } from "../../hooks/table-emission-hooks/use-precos-processados"
import { useRatingDevedorTable } from "../../hooks/table-emission-hooks/use-last-debtor-table"
import { useIndiceLiquidezTable } from "../../hooks/table-emission-hooks/use-last-index-liquidity"
import { useUltimoNegocioTable } from "../../hooks/table-emission-hooks/use-last-business"
import { useUltimoEventoCreditoEmissao } from "../../hooks/table-emission-hooks/use-last-event-credit-emission"
import { useUltimoEventoCreditoDevedor } from "../../hooks/table-emission-hooks/use-last-event-debtor"
import LoaderOverlay from "src/v2/components/LoaderOverlay/loader-overlay"
import Loader from "src/v2/components/Loader/loader"
import SummarySection from "src/v2/components/SummarySection/summary-section"
import { getSummarySections } from "./constants/summary-sections"
import { getEmissionSummaryModalConfig } from "./configs/emission-modal-config"
import EmissionDetailsModal from "../emission-details-modal/emission-details-modal"
import { getEmissionDetailsModalConfig } from "./configs/get-emission-details-modal-config"

export const EmissionSummarySections = ({
    cetipIsin,
    emissaoDatalakeService,
    characterData,
    contraparteData,
    garantiasData,
    offerData,
    fatosRelevantesData,
    admin,
}) => {
    const data = characterData || {}
    const ofertaData = offerData || {}
    const fatosData = Array.isArray(fatosRelevantesData) ? fatosRelevantesData : []
    
    const cpfCnpj = contraparteData?.CPFCNPJ_Devedor || data?.CPFCNPJ_Devedor
    
    const [isFetchingQueue, setIsFetchingQueue] = useState(false)

    const [modalIsVisible, setModalIsVisible] = useState(false)
    const [modalType, setModalType] = useState("")

    const [detailsModalVisible, setDetailsModalVisible] = useState(false)
    const [detailsModalType, setDetailsModalType] = useState("")

    const { state } = useBoxSectionRating(
        cetipIsin,
        cpfCnpj,
        emissaoDatalakeService
    )

    const { state: stateRating, actions: ratingActions } = useRatingEmissaoTable(
        emissaoDatalakeService,
        cetipIsin,
        data
    )

    const { state: statePriceProcess, actions: priceProcessActions } = usePrecosProcessados(
        emissaoDatalakeService,
        cetipIsin,
        data
    )

    const { state: stateDebtorEmission, actions: debtorEmissionActions } = useRatingDevedorTable(
        emissaoDatalakeService,
        cpfCnpj,
        data
    )

    const { state: stateIndexLiquidity, actions: indexLiquidityActions } = useIndiceLiquidezTable(
        emissaoDatalakeService,
        cetipIsin,
        data
    )

    const { state: lastBusinessState, actions: lastBusinessActions } = useUltimoNegocioTable(
        emissaoDatalakeService,
        cetipIsin,
        data
    )

    const {
        state: lastEventCreditEmissionState,
        actions: lastEventCreditEmissionActions,
    } = useUltimoEventoCreditoEmissao(
        emissaoDatalakeService,
        cetipIsin,
        data
    )

    const {
        state: lastEventCreditDebtorState,
        actions: lastEventCreditDebtorActions,
    } = useUltimoEventoCreditoDevedor(
        emissaoDatalakeService,
        cpfCnpj,
        data,
        cetipIsin
    )

    useEffect(() => {
        if (!cetipIsin) return

        const fetchEmFila = async () => {
            setIsFetchingQueue(true)

            try {
                await Promise.allSettled([
                    ratingActions.loadTableRows(),
                    lastEventCreditEmissionActions.loadTableRows(),
                ])

                await priceProcessActions.loadTableRows()
                await indexLiquidityActions.loadTableRows()
                await lastBusinessActions.loadTableRows()

            } finally {
                setIsFetchingQueue(false)
            }
        }

        fetchEmFila()
    }, [cetipIsin])

    useEffect(() => {
        if (!cpfCnpj) return

        Promise.allSettled([
            debtorEmissionActions.loadTableRows(),
            lastEventCreditDebtorActions.loadTableRows(),
        ])
    }, [cpfCnpj])

    const isLoading =
        isFetchingQueue ||
        state.loading ||
        stateRating.loading ||
        statePriceProcess.loading ||
        stateDebtorEmission.loading ||
        stateIndexLiquidity.loading ||
        lastBusinessState.loading ||
        lastEventCreditEmissionState.loading ||
        lastEventCreditDebtorState.loading

    const modalConfig = getEmissionSummaryModalConfig({
        admin,
        stateRating,
        ratingActions,
        stateDebtorEmission,
        debtorEmissionActions,
        lastEventCreditEmissionState,
        lastEventCreditEmissionActions,
        lastEventCreditDebtorState,
        lastEventCreditDebtorActions,
        stateIndexLiquidity,
        indexLiquidityActions,
        lastBusinessState,
        lastBusinessActions,
        statePriceProcess,
        priceProcessActions,
    })

    const detailsModalConfig = getEmissionDetailsModalConfig({
        characterData: data,
        garantiasData,
        fatosRelevantesData: fatosData,
        offerData: ofertaData,
    })

    const handleClickSummaryModal = async (value) => {
        if (detailsModalConfig[value]) {
            setDetailsModalType(value)
            setDetailsModalVisible(true)
            return
        }

        if (!modalConfig[value]) return

        setModalType(value)
        setModalIsVisible(true)
    }

    const selectedModalConfig = modalType ? modalConfig[modalType] : null
    const selectedDetailsModalConfig = detailsModalType ? detailsModalConfig[detailsModalType] : null

    const ratingSummarySections = getSummarySections({
        garantiasData,
        fatosRelevantesData: fatosData,
        offerData: ofertaData,
        handleClickEmission: handleClickSummaryModal,
        stateRating,
        stateDebtorEmission,
        statePriceProcess,
        lastBusinessState,
        stateIndexLiquidity,
        lastEventCreditEmissionState,
        lastEventCreditDebtorState,
    })

    return (
        <>
            {isLoading && (
                <LoaderOverlay>
                    <Loader />
                </LoaderOverlay>
            )}

            <EmissionDetailsModal
                open={modalIsVisible}
                onClose={() => {
                    setModalIsVisible(false)
                    setModalType("")
                }}
                title={selectedModalConfig?.title ?? ""}
                sections={selectedModalConfig?.sections ?? []}
                empty={selectedModalConfig?.empty ?? false}
                emptyText={selectedModalConfig?.emptyText ?? ""}
            />

            <EmissionDetailsModal
                open={detailsModalVisible}
                onClose={() => {
                    setDetailsModalVisible(false)
                    setDetailsModalType("")
                }}
                title={selectedDetailsModalConfig?.title ?? ""}
                sections={selectedDetailsModalConfig?.sections ?? []}
                empty={selectedDetailsModalConfig?.empty ?? false}
                emptyText={selectedDetailsModalConfig?.emptyText ?? ""}
            />

            <Box>
                <Grid container rowSpacing={3}>
                    {ratingSummarySections.map((section) => (
                        <Grid
                            key={section.title}
                            item
                            {...section.gridProps}
                            sx={{
                                display: "flex",
                                ...(section.sx ?? {}),
                            }}
                        >
                            <SummarySection
                                title={section.title}
                                fields={section.fields}
                                empty={section.empty}
                                emptyText={section.emptyText}
                                onClick={section.onClick}
                                fieldGridProps={section.fieldGridProps}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </>
    )
}

EmissionSummarySections.propTypes = {
    cetipIsin: PropTypes.string,
    emissaoDatalakeService: PropTypes.any,
    characterData: PropTypes.any,
    contraparteData: PropTypes.any,
    garantiasData: PropTypes.any,
    offerData: PropTypes.any,
    fatosRelevantesData: PropTypes.array,
    admin: PropTypes.bool,
}